import React from "react";
import { Link } from "react-router-dom";
import { Award, BookOpen, Users, Compass, Globe2, Building2, ArrowRight, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge"; // eslint-disable-line no-unused-vars
import { PageHero, Section, SectionHeading } from "@/components/blocks";


const EXPERTISE = [
  { icon: Compass, label: "Placements" },
  { icon: Users, label: "Mentoring" },
  { icon: BookOpen, label: "Behaviour Management" },
  { icon: Award, label: "Employability" },
  { icon: Globe2, label: "International Teacher Support" },
  { icon: Building2, label: "School–University Partnerships" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow="About Teaching Pathways"
        title="Built by educators, for educators."
        subtitle="Teaching Pathways exists to empower every person in the teaching journey — from the pre-service teacher on their first placement to the schools and universities that shape the profession."
        image="https://images.pexels.com/photos/5940842/pexels-photo-5940842.jpeg?auto=compress&cs=tinysrgb&w=1600"
      />

      <Section>
        <div className="grid lg:grid-cols-5 gap-12 items-start">
          <div className="lg:col-span-2">
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
              <img src="https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=800" alt="The Teaching Pathways team in discussion" className="w-full h-[420px] object-cover" />
            </div>
          </div>
          <div className="lg:col-span-3">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 font-display">Our team</h2>
            <div className="mt-6 space-y-4 text-slate-600 leading-relaxed">
              <p>Teaching Pathways was created by experienced teachers, mentors and university educators who understand the realities of entering the teaching profession.</p>
              <p>Having supported hundreds of pre-service teachers through placements, graduate applications, interview preparation and the transition into schools, our mission is simple: to provide practical, honest and accessible support for those building a career in education.</p>
              <p>Our team combines classroom experience, teacher education expertise and mentoring knowledge to help aspiring educators navigate each stage of their journey with confidence.</p>
              <p>We are particularly passionate about supporting international pre-service teachers and educators transitioning into Australian and New Zealand school systems, helping them develop the professional skills, confidence and cultural understanding needed to thrive.</p>
              <p>Whether you're preparing for placement, applying for your first teaching position, building a professional portfolio or seeking guidance from experienced educators, Teaching Pathways exists to help you take the next step with clarity and confidence.</p>
            </div>
          </div>
        </div>
      </Section>

      <div className="bg-slate-50 border-y border-slate-200">
        <Section>
          <SectionHeading eyebrow="Areas of Expertise" title="Deep, practical experience across the profession" center />
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {EXPERTISE.map((e) => (
              <Card key={e.label} className="p-6 flex items-center gap-4 border-slate-200" data-testid={`expertise-${e.label}`}>
                <div className="w-11 h-11 rounded-xl bg-teal-600 flex items-center justify-center shrink-0"><e.icon className="w-5 h-5 text-white" /></div>
                <span className="font-semibold text-slate-800">{e.label}</span>
              </Card>
            ))}
          </div>
        </Section>
      </div>

      <Section>
        <div className="grid md:grid-cols-2 gap-8">
          <div className="rounded-2xl bg-slate-900 p-10 text-white">
            <GraduationCap className="w-9 h-9 text-teal-400 mb-4" />
            <h3 className="text-2xl font-bold font-display mb-3">Our Philosophy</h3>
            <p className="text-slate-300 leading-relaxed">Great teachers are made through preparation, reflection and support — not thrown in the deep end. We believe every educator deserves a mentor, a plan and a community. Confidence is built, not assumed.</p>
          </div>
          <div className="rounded-2xl bg-emerald-600 p-10 text-white">
            <Compass className="w-9 h-9 text-emerald-100 mb-4" />
            <h3 className="text-2xl font-bold font-display mb-3">Our Mission</h3>
            <p className="text-emerald-50 leading-relaxed">To empower pre-service teachers, graduate teachers, international educators, mentor teachers, schools, universities and families through professional learning, employability support, mentoring, partnerships and educational opportunities.</p>
          </div>
        </div>
        <div className="text-center mt-12">
          <Button asChild size="lg" className="bg-teal-600 hover:bg-teal-700 rounded-full px-8"><Link to="/contact" data-testid="about-cta">Work with us <ArrowRight className="w-4 h-4" /></Link></Button>
        </div>
      </Section>
    </div>
  );
}
